import { useEffect, useRef, useState } from 'react';

type Primitive = {
  id: string;
  label: string;
  color: string;
  status: string;
  statusColor: string;
  /** target Y at the right edge (svg coords) — controls ray angle */
  targetY: number;
};

const PRIMITIVES: Primitive[] = [
  {
    id: 'auth',
    label: 'Auth',
    color: '#7C3AED',
    status: 'Live',
    statusColor: '#10B981',
    targetY: 80,
  },
  {
    id: 'store',
    label: 'Store',
    color: '#06B6D4',
    status: 'Beta',
    statusColor: '#06B6D4',
    targetY: 175,
  },
  {
    id: 'comms',
    label: 'Comms',
    color: '#D97706',
    status: 'Soon',
    statusColor: '#94A3B8',
    targetY: 270,
  },
  {
    id: 'realtime',
    label: 'Realtime',
    color: '#EC4899',
    status: 'Soon',
    statusColor: '#94A3B8',
    targetY: 365,
  },
];

/**
 * Interactive prism diagram — the brand metaphor in motion. Single white beam
 * enters from the left, refracts through the prism, splits into four colored
 * primitives at the right. Mouse-tilt 3D parallax, animated draw-in on mount,
 * hover any primitive endpoint to highlight + dim the others.
 *
 * Built deliberately abstract (geometric outline triangle, not a rendered glass
 * object) to avoid the mid-tier-template trap the senior designer flagged.
 */
export default function PrismDiagram() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const onMove = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // -1..1 normalized
    const nx = (e.clientX - rect.left) / rect.width - 0.5;
    const ny = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: nx * 8, y: ny * -8 }); // max 8deg
  };

  const onLeave = () => setTilt({ x: 0, y: 0 });

  const PRISM_APEX = { x: 290, y: 130 };
  const PRISM_BASE_LEFT = { x: 220, y: 280 };
  const PRISM_BASE_RIGHT = { x: 360, y: 280 };
  // Light enters at the left side of the prism, around mid-height
  const ENTRY = { x: 252, y: 220 };
  // All rays exit from the right edge of the prism (mid)
  const EXIT = { x: 340, y: 220 };

  return (
    <div
      ref={containerRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative w-full select-none"
      style={{ perspective: '1200px' }}
    >
      {/* ambient halo behind the prism */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 mx-auto h-[420px] max-w-[820px] opacity-70 blur-3xl"
        style={{
          background:
            'radial-gradient(closest-side, rgba(124,58,237,0.18) 0%, rgba(236,72,153,0.10) 55%, transparent 80%)',
          transform: `translate3d(${tilt.x * 4}px, ${tilt.y * 4}px, 0)`,
        }}
      />

      <svg
        viewBox="0 0 820 380"
        width="100%"
        className="relative h-auto"
        style={{
          transform: `rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
          transition: 'transform 220ms ease-out',
          transformStyle: 'preserve-3d',
        }}
      >
        <defs>
          {/* Soft white→spectrum gradient for the inside of the prism */}
          <linearGradient id="prism-fill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgb(var(--surface))" stopOpacity="0.6" />
            <stop offset="55%" stopColor="rgb(var(--violet-soft))" stopOpacity="0.85" />
            <stop offset="100%" stopColor="rgb(var(--pink-soft))" stopOpacity="0.7" />
          </linearGradient>
          {/* Hairline stroke gradient — violet at apex, cyan at base */}
          <linearGradient id="prism-stroke" x1="0.5" y1="0" x2="0.5" y2="1">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#06B6D4" />
          </linearGradient>
          {/* Glow filter for the rays */}
          <filter id="ray-glow" x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Subtle hairline grid behind */}
        <g opacity="0.35">
          {Array.from({ length: 7 }, (_, i) => (
            <line
              key={i}
              x1={i * 100}
              y1={0}
              x2={i * 100}
              y2={380}
              stroke="rgb(var(--border))"
              strokeWidth={1}
              strokeDasharray="2 6"
            />
          ))}
        </g>

        {/* Incoming light beam */}
        <line
          x1={20}
          y1={ENTRY.y}
          x2={ENTRY.x}
          y2={ENTRY.y}
          stroke="rgb(var(--ink))"
          strokeWidth={2}
          strokeLinecap="round"
          opacity={mounted ? 0.85 : 0}
          style={{ transition: 'opacity 600ms ease-out' }}
        />
        {/* incoming "your app" label */}
        <text
          x={28}
          y={ENTRY.y - 12}
          fill="rgb(var(--muted))"
          fontFamily="JetBrains Mono"
          fontSize={11}
          opacity={mounted ? 1 : 0}
          style={{ transition: 'opacity 800ms ease-out' }}
        >
          your app
        </text>

        {/* Diverging rays — drawn before prism so prism overlaps them */}
        {PRIMITIVES.map((p, i) => {
          const dim = hovered && hovered !== p.id;
          const lift = hovered === p.id;
          return (
            <g
              key={p.id}
              onMouseEnter={() => setHovered(p.id)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'pointer' }}
            >
              <line
                x1={EXIT.x}
                y1={EXIT.y}
                x2={620}
                y2={p.targetY}
                stroke={p.color}
                strokeWidth={lift ? 4 : 3}
                strokeLinecap="round"
                opacity={mounted ? (dim ? 0.18 : 0.92) : 0}
                style={{
                  transition: 'opacity 600ms ease-out, stroke-width 240ms ease-out',
                  transitionDelay: `${600 + i * 120}ms`,
                  filter: lift ? 'url(#ray-glow)' : undefined,
                }}
                className="ray-fade"
              />
            </g>
          );
        })}

        {/* The prism — geometric outline, low opacity fill */}
        <polygon
          points={`${PRISM_APEX.x},${PRISM_APEX.y} ${PRISM_BASE_RIGHT.x},${PRISM_BASE_RIGHT.y} ${PRISM_BASE_LEFT.x},${PRISM_BASE_LEFT.y}`}
          fill="url(#prism-fill)"
          stroke="url(#prism-stroke)"
          strokeWidth={1.5}
          opacity={mounted ? 1 : 0}
          style={{ transition: 'opacity 800ms ease-out' }}
        />
        {/* prism inner highlight line (the "edge" facing the viewer) */}
        <line
          x1={PRISM_APEX.x}
          y1={PRISM_APEX.y}
          x2={PRISM_BASE_RIGHT.x}
          y2={PRISM_BASE_RIGHT.y}
          stroke="rgb(var(--surface))"
          strokeWidth={1}
          opacity={mounted ? 0.7 : 0}
          style={{ transition: 'opacity 800ms ease-out' }}
        />

        {/* Endpoint chips — labeled, clickable, lift on hover */}
        {PRIMITIVES.map((p, i) => {
          const dim = hovered && hovered !== p.id;
          const lift = hovered === p.id;
          return (
            <g
              key={`chip-${p.id}`}
              onMouseEnter={() => setHovered(p.id)}
              onMouseLeave={() => setHovered(null)}
              style={{
                cursor: 'pointer',
                transform: `translateX(${lift ? 4 : 0}px)`,
                transition: 'transform 200ms ease-out',
              }}
            >
              {/* halo ring */}
              <circle
                cx={620}
                cy={p.targetY}
                r={lift ? 18 : 12}
                fill={p.color}
                opacity={lift ? 0.18 : 0}
                style={{ transition: 'r 220ms ease-out, opacity 220ms ease-out' }}
              />
              <circle
                cx={620}
                cy={p.targetY}
                r={9}
                fill="rgb(var(--surface))"
                stroke={p.color}
                strokeWidth={2}
                opacity={mounted ? (dim ? 0.4 : 1) : 0}
                style={{
                  transition: 'opacity 600ms ease-out',
                  transitionDelay: `${1000 + i * 120}ms`,
                }}
              />
              <circle
                cx={620}
                cy={p.targetY}
                r={4}
                fill={p.color}
                opacity={mounted ? (dim ? 0.4 : 1) : 0}
                style={{
                  transition: 'opacity 600ms ease-out',
                  transitionDelay: `${1000 + i * 120}ms`,
                }}
              />
              {/* label group */}
              <g
                opacity={mounted ? (dim ? 0.4 : 1) : 0}
                style={{
                  transition: 'opacity 600ms ease-out',
                  transitionDelay: `${1100 + i * 120}ms`,
                }}
              >
                <text
                  x={638}
                  y={p.targetY - 4}
                  fill="rgb(var(--ink))"
                  fontFamily="Inter"
                  fontSize={14}
                  fontWeight={700}
                >
                  {p.label}
                </text>
                <text
                  x={638}
                  y={p.targetY + 12}
                  fill={p.statusColor}
                  fontFamily="JetBrains Mono"
                  fontSize={10}
                  fontWeight={700}
                >
                  {p.status.toUpperCase()}
                </text>
              </g>
            </g>
          );
        })}
      </svg>

      {/* Caption under the diagram */}
      <p className="mt-2 text-center font-mono text-[11px] text-muted">
        one prism · four primitives · hover any ray
      </p>
    </div>
  );
}
